import type * as Y from 'yjs'
import { DEFAULT_FRAME_STYLE, type BoardElement, type Frame, type MediaDto, type TemplateContent } from '@praesto/shared'
import { addElement, createId, nextZ } from './elements.js'
import { addFrame, createFrameId, nextFrameOrder } from './frames.js'

/**
 * Eine Vorlage auf dem Board absetzen.
 *
 * Die Elemente der Vorlage liegen relativ zur linken oberen Frame-Ecke; hier
 * bekommen sie Board-Koordinaten, frische IDs und — für Bilder und Videos — die
 * ID der Kopie, die der Server für dieses Board angelegt hat. Alles in einer
 * einzigen Transaktion: Ein halb eingefügter Frame wäre für Mitarbeitende an
 * demselben Board ein sichtbarer Zwischenzustand, und für die Rücknahme über
 * „Rückgängig" ein einzelner Schritt statt zwanzig.
 */
export function insertTemplate(
  doc: Y.Doc,
  content: TemplateContent,
  media: Record<string, MediaDto>,
  name: string,
  at: { x: number; y: number },
): string {
  const frameId = createFrameId()
  const baseZ = nextZ(doc)

  doc.transact(() => {
    const frame: Frame = {
      id: frameId,
      name,
      x: at.x,
      y: at.y,
      w: content.width,
      h: content.height,
      order: nextFrameOrder(doc),
      background: content.background ?? '',
      borderColor: content.borderColor ?? '#e8622e',
      borderWidth: content.borderWidth,
      // Satzspiegel und Raster bleiben automatisch: Sie leiten sich aus der
      // Größe ab und passen deshalb auch dort, wo die Vorlage abgesetzt wird.
      padding: DEFAULT_FRAME_STYLE.padding,
      columns: DEFAULT_FRAME_STYLE.columns,
      rows: DEFAULT_FRAME_STYLE.rows,
    }
    addFrame(doc, frame)

    content.elements.forEach((element, index) => {
      const placed = {
        ...element,
        id: createId(),
        x: at.x + element.x,
        y: at.y + element.y,
        z: baseZ + index,
      } as BoardElement

      if ('mediaId' in placed) {
        const copy = media[element.type === 'image' || element.type === 'video' ? element.mediaId : '']
        // Fehlt die Kopie, wird das Element ausgelassen statt mit einer toten
        // Referenz eingefügt: ein leeres Rechteck erklärt niemandem, was fehlt.
        if (!copy) return
        placed.mediaId = copy.id
      }

      addElement(doc, placed)
    })
  })

  return frameId
}
